import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  Clock,
  CheckCircle2,
  Package,
  Truck,
  XCircle,
  Loader2,
  RefreshCw,
  Phone,
  MapPin,
  CreditCard,
  ChefHat,
  PlusCircle,
  Link as LinkIcon,
  Copy,
  Power,
  ExternalLink,
  QrCode,
  Filter,
  BarChart3,
  TicketPercent,
  Gift,
  Send,
  Volume2,
  VolumeX,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import { api } from "../services/api";
import { playOrderAlert } from "../utils/audioAlert";
import DeliveryNav from "../components/delivery/DeliveryNav";
import AssinaturaBanner from "../components/delivery/AssinaturaBanner";
import PushIndicator from "../components/delivery/PushIndicator";

// Colunas do Kanban (ordem visual). "cancelado" fica em pé separado embaixo.
const COLUNAS = [
  { key: "recebido", label: "Novos", icon: Bell, color: "bg-amber-100 text-amber-700 border-amber-200" },
  { key: "aceito", label: "Aceitos", icon: CheckCircle2, color: "bg-blue-100 text-blue-700 border-blue-200" },
  { key: "em_preparo", label: "Em preparo", icon: ChefHat, color: "bg-orange-100 text-orange-700 border-orange-200" },
  { key: "pronto", label: "Prontos", icon: Package, color: "bg-purple-100 text-purple-700 border-purple-200" },
  { key: "entregue", label: "Entregues", icon: Truck, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
];

const PROXIMOS_STATUS = {
  recebido: "aceito",
  aceito: "em_preparo",
  em_preparo: "pronto",
  pronto: "entregue",
};

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const tempoRelativo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export default function DeliveryKanban() {
  const { negocioId } = useParams();
  const navigate = useNavigate();

  const [loja, setLoja] = useState(null);
  const [negocio, setNegocio] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingLoja, setCreatingLoja] = useState(false);
  const [publicLink, setPublicLink] = useState("");
  const [filterMode, setFilterMode] = useState("today"); // today | all | 7days
  const [qrUrl, setQrUrl] = useState("");
  const [showQrModal, setShowQrModal] = useState(false);
  const [loyalty, setLoyalty] = useState(null); // {pedido, result} when modal open
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  // Som habilitado? Persiste em localStorage. Default = on.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("dlv-sound") !== "off"; }
    catch { return true; }
  });
  // Rastreia IDs já vistos para detectar pedidos novos sem reload.
  const seenIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  const loadNegocio = useCallback(async () => {
    try {
      const r = await api.get(`/businesses/${encodeURIComponent(negocioId)}`);
      setNegocio(r.data);
    } catch {
      toast.error("Negócio não encontrado");
      navigate("/meus-negocios");
    }
  }, [negocioId, navigate]);

  const loadLoja = useCallback(async () => {
    try {
      const l = await deliveryApi.getLojaByNegocio(negocioId);
      setLoja(l);
      return l;
    } catch (err) {
      if (err?.response?.status === 404) {
        setLoja(null);
        return null;
      }
      toast.error("Erro ao carregar loja Delivery");
      return null;
    }
  }, [negocioId]);

  const loadPedidos = useCallback(async (lojaId, mode = "today") => {
    if (!lojaId) return;
    try {
      const params = { loja_id: lojaId, limit: 200 };
      if (mode === "today") {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        params.since = d.toISOString();
      } else if (mode === "7days") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        params.since = d.toISOString();
      }
      const list = await deliveryApi.listPedidos(params);

      // Detecta pedidos novos (status `recebido` que ainda não vimos)
      const prevSeen = seenIdsRef.current;
      const currentIds = new Set(list.map((p) => p.id));
      if (!firstLoadRef.current && prevSeen) {
        const novos = list.filter(
          (p) => p.status === "recebido" && !prevSeen.has(p.id)
        );
        if (novos.length > 0) {
          if (soundEnabled) playOrderAlert(novos.length === 1 ? 2 : 3);
          // Toast destacado por pedido novo (max 3 simultâneos)
          novos.slice(0, 3).forEach((p) => {
            toast.success(
              `🛎️ Novo pedido ${p.numero_curto} — ${(p.cliente?.nome || "Cliente")}`,
              {
                id: `dlv-novo-${p.id}`,
                duration: 8000,
                description: `Total ${formatBRL(p.total)} • ${p.tipo === "entrega" ? "Entrega" : "Retirada"}`,
              }
            );
          });
          if (novos.length > 3) {
            toast.info(`+ ${novos.length - 3} novos pedidos recebidos`);
          }
        }
      }
      seenIdsRef.current = currentIds;
      firstLoadRef.current = false;

      setPedidos(list);
    } catch {
      toast.error("Erro ao buscar pedidos");
    }
  }, [soundEnabled]);

  // Toggle som persistido em localStorage
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem("dlv-sound", next ? "on" : "off"); }
      catch { /* ignore */ }
      if (next) {
        // Toca um beep curto para confirmar e desbloquear o audio context.
        playOrderAlert(1);
        toast.success("Alerta sonoro ativado");
      } else {
        toast.info("Alerta sonoro silenciado");
      }
      return next;
    });
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadNegocio();
      const l = await loadLoja();
      if (l) await loadPedidos(l.id, filterMode);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadNegocio, loadLoja, loadPedidos]);

  // Auto refresh dos pedidos a cada 20s
  useEffect(() => {
    if (!loja?.id) return;
    const t = setInterval(() => loadPedidos(loja.id, filterMode), 20000);
    return () => clearInterval(t);
  }, [loja?.id, filterMode, loadPedidos]);

  // Recarrega ao trocar filtro
  useEffect(() => {
    if (loja?.id) loadPedidos(loja.id, filterMode);
  }, [filterMode, loja?.id, loadPedidos]);

  // Link público + QR
  useEffect(() => {
    if (negocio?.municipio_slug && negocio?.slug) {
      const origin = window.location.origin;
      setPublicLink(`${origin}/delivery/${negocio.municipio_slug}/${negocio.slug}`);
    }
  }, [negocio]);

  useEffect(() => {
    if (loja?.id) {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      setQrUrl(`${apiBase}/api/delivery/lojas/${loja.id}/qrcode.png?size=12`);
    }
  }, [loja?.id]);

  const handleCreateLoja = async () => {
    if (!negocio) return;
    setCreatingLoja(true);
    try {
      const created = await deliveryApi.createLoja({
        negocio_id: negocio.id,
        municipio_id: negocio.municipio_id,
        nome_loja: negocio.nome || "Minha Loja",
        descricao: negocio.descricao_curta || "",
        tempo_medio_preparo_min: 30,
      });
      setLoja(created);
      toast.success("Delivery ativado! Cadastre seus produtos e zonas.");
      await loadPedidos(created.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao ativar Delivery");
    } finally {
      setCreatingLoja(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!loja) return;
    const novoEstado = !loja.ativo;
    try {
      await deliveryApi.toggleLojaStatus(loja.id, novoEstado);
      setLoja({ ...loja, ativo: novoEstado });
      toast.success(novoEstado ? "Loja ativa para receber pedidos" : "Loja pausada");
    } catch {
      toast.error("Falha ao alterar status");
    }
  };

  const handleAdvance = async (p) => {
    const prox = PROXIMOS_STATUS[p.status];
    if (!prox) return;
    try {
      const upd = await deliveryApi.updateStatus(p.id, prox);
      setPedidos((prev) => prev.map((x) => (x.id === p.id ? upd : x)));
      toast.success(`Pedido movido para ${prox}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível avançar");
    }
  };

  const handleCancel = async (p) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Cancelar este pedido?")) return;
    try {
      const upd = await deliveryApi.updateStatus(p.id, "cancelado", "Cancelado pelo lojista");
      setPedidos((prev) => prev.map((x) => (x.id === p.id ? upd : x)));
      toast.success("Pedido cancelado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível cancelar");
    }
  };

  const copyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const handleOpenLoyalty = async (pedido) => {
    setLoyalty({ pedido });
    setLoyaltyLoading(true);
    try {
      const r = await deliveryApi.fidelidadeCupom1Click(pedido.id, { desconto_percent: 10 });
      setLoyalty({ pedido, result: r });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível criar o cupom");
      setLoyalty(null);
    } finally {
      setLoyaltyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-secondary)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  // ===== Estado: sem loja Delivery ainda =====
  if (!loja) {
    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/meus-negocios"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] mb-6"
            data-testid="delivery-back-meus-negocios"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar para meus negócios
          </Link>

          <div className="bg-white rounded-2xl border border-[var(--border)] p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shrink-0">
                <Package className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-2" data-testid="delivery-onboarding-title">
                  Ativar Empreende Delivery
                </h1>
                <p className="text-[var(--text-secondary)] mb-4">
                  Receba pedidos online direto pelo seu cardápio público, com painel
                  Kanban em tempo real. Sem precisar de aplicativo de terceiros — e
                  sem repassar comissão pesada.
                </p>
                <ul className="text-sm text-[var(--text-secondary)] space-y-1 mb-6 list-disc list-inside">
                  <li>Cardápio web próprio (link compartilhável + QR Code)</li>
                  <li>Notificação na hora que chega o pedido</li>
                  <li>Kanban: novo → aceito → preparo → pronto → entregue</li>
                  <li>Zonas de entrega com taxa configurável</li>
                </ul>
                <button
                  onClick={handleCreateLoja}
                  disabled={creatingLoja}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                  data-testid="delivery-activate-button"
                >
                  {creatingLoja ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <PlusCircle className="w-4 h-4" />
                  )}
                  Ativar Delivery agora
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Kanban =====
  const pedidosByStatus = {};
  COLUNAS.forEach((c) => (pedidosByStatus[c.key] = []));
  pedidos.forEach((p) => {
    if (pedidosByStatus[p.status]) pedidosByStatus[p.status].push(p);
  });
  const cancelados = pedidos.filter((p) => p.status === "cancelado");

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12">
      <DeliveryNav
        negocioId={negocioId}
        negocioNome={loja.nome_loja || negocio?.nome}
        publicMenuUrl={publicLink}
      />
      <AssinaturaBanner negocioId={negocioId} />
      {/* Header — controles específicos do Kanban */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--text-secondary)]" data-testid="delivery-kanban-title">
              {pedidos.length} pedido(s) carregado(s)
            </p>
          </div>

          <button
            onClick={() => loadPedidos(loja.id, filterMode)}
            className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg"
            title="Atualizar"
            data-testid="delivery-refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            onClick={toggleSound}
            className={`p-2 rounded-lg border transition ${
              soundEnabled
                ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                : "bg-[var(--bg-secondary)] border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--border)]"
            }`}
            title={soundEnabled ? "Silenciar alerta sonoro" : "Ativar alerta sonoro"}
            data-testid="delivery-toggle-sound"
            aria-pressed={soundEnabled}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>

          <PushIndicator municipioId={loja?.municipio_id} />

          <button
            onClick={handleToggleStatus}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border ${
              loja.ativo
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
            data-testid="delivery-toggle-status"
          >
            <Power className="w-4 h-4" />
            {loja.ativo ? "Loja ativa" : "Loja pausada"}
          </button>
        </div>

        {/* Link público */}
        {publicLink && (
          <div className="max-w-7xl mx-auto px-4 pb-3 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <LinkIcon className="w-3.5 h-3.5" />
            <span className="truncate font-mono">{publicLink}</span>
            <button
              onClick={copyLink}
              className="px-2 py-1 hover:bg-[var(--bg-secondary)] rounded"
              data-testid="delivery-copy-link"
              title="Copiar link"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <a
              href={publicLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 py-1 hover:bg-[var(--bg-secondary)] rounded"
              data-testid="delivery-open-link"
              title="Abrir cardápio público"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => setShowQrModal(true)}
              className="px-2 py-1 hover:bg-[var(--bg-secondary)] rounded"
              title="Ver QR Code"
              data-testid="delivery-qr-button"
            >
              <QrCode className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Filtros */}
        <div className="max-w-7xl mx-auto px-4 pb-2 flex items-center gap-2 text-xs">
          <Filter className="w-3.5 h-3.5 text-[var(--text-secondary)]" />
          <span className="text-[var(--text-secondary)]">Período:</span>
          {[
            { key: "today", label: "Hoje" },
            { key: "7days", label: "Últimos 7 dias" },
            { key: "all", label: "Todos" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilterMode(opt.key)}
              className={`px-2.5 py-1 rounded-full border ${
                filterMode === opt.key
                  ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                  : "bg-white border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
              }`}
              data-testid={`delivery-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* QR Code modal */}
      {showQrModal && qrUrl && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowQrModal(false)}
          data-testid="delivery-qr-modal"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg mb-2 text-center">QR Code do seu cardápio</h3>
            <p className="text-xs text-[var(--text-secondary)] text-center mb-4">
              Imprima e coloque no balcão ou em embalagens. Clientes escaneiam e
              fazem pedido direto.
            </p>
            <div className="bg-white p-4 border border-[var(--border)] rounded-xl flex items-center justify-center">
              <img
                src={qrUrl}
                alt="QR Code do cardápio"
                className="w-64 h-64"
                data-testid="delivery-qr-image"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <a
                href={qrUrl}
                download={`cardapio-${negocio?.slug || "loja"}.png`}
                className="flex-1 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium text-center hover:opacity-90"
                data-testid="delivery-qr-download"
              >
                Baixar PNG
              </a>
              <button
                onClick={() => setShowQrModal(false)}
                className="px-4 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="delivery-qr-close"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loyalty Modal — 1-click cupom de fidelidade */}
      {loyalty && (
        <LoyaltyModal
          loyalty={loyalty}
          loading={loyaltyLoading}
          onClose={() => setLoyalty(null)}
        />
      )}

      {/* Kanban Colunas */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {COLUNAS.map((col) => {
            const Icon = col.icon;
            const lista = pedidosByStatus[col.key];
            return (
              <div key={col.key} className="bg-white rounded-xl border border-[var(--border)] flex flex-col">
                <div className={`px-3 py-2 border-b ${col.color} rounded-t-xl flex items-center justify-between`}>
                  <div className="flex items-center gap-2 font-semibold text-sm">
                    <Icon className="w-4 h-4" /> {col.label}
                  </div>
                  <span className="text-xs font-bold" data-testid={`delivery-col-count-${col.key}`}>
                    {lista.length}
                  </span>
                </div>
                <div className="p-2 flex-1 min-h-[300px] space-y-2 overflow-auto">
                  {lista.length === 0 ? (
                    <p className="text-xs text-center text-[var(--text-secondary)] py-6">
                      —
                    </p>
                  ) : (
                    lista.map((p) => (
                      <PedidoCard
                        key={p.id}
                        pedido={p}
                        onAdvance={handleAdvance}
                        onCancel={handleCancel}
                        onLoyalty={handleOpenLoyalty}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Cancelados */}
        {cancelados.length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-[var(--border)] p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-red-600">
              <XCircle className="w-4 h-4" />
              Cancelados ({cancelados.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {cancelados.map((p) => (
                <PedidoCard
                  key={p.id}
                  pedido={p}
                  onAdvance={() => {}}
                  onCancel={() => {}}
                  readOnly
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PedidoCard({ pedido, onAdvance, onCancel, onLoyalty, readOnly = false }) {
  const proxStatus = PROXIMOS_STATUS[pedido.status];
  const endereco = pedido.cliente?.endereco_entrega;

  return (
    <div
      className="bg-[var(--bg-secondary)] rounded-lg p-3 text-sm border border-transparent hover:border-[var(--primary)] transition-colors"
      data-testid={`delivery-pedido-card-${pedido.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="font-bold">{pedido.numero_curto}</span>
          {pedido.tem_encomenda && (
            <span
              className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full"
              title={pedido.data_entrega_mais_proxima ? `Entrega em ${pedido.data_entrega_mais_proxima}` : ""}
              data-testid={`delivery-encomenda-badge-${pedido.id}`}
            >
              📅 Encomenda{pedido.data_entrega_mais_proxima ? ` ${pedido.data_entrega_mais_proxima}` : ""}
            </span>
          )}
        </div>
        <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
          <Clock className="w-3 h-3" /> {tempoRelativo(pedido.created_at)}
        </span>
      </div>
      <div className="font-medium truncate flex items-center gap-1.5">
        <span className="truncate">{pedido.cliente?.nome}</span>
        {pedido.cliente_pedidos_count > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && onLoyalty) onLoyalty(pedido);
            }}
            disabled={readOnly}
            className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full shrink-0 hover:bg-amber-200 transition-colors disabled:hover:bg-amber-100 inline-flex items-center gap-1"
            title={readOnly ? `${pedido.cliente_pedidos_count}º pedido` : "Criar cupom de fidelidade"}
            data-testid={`delivery-loyalty-trigger-${pedido.id}`}
          >
            🎁 {pedido.cliente_pedidos_count}º · Recorrente
          </button>
        )}
      </div>
      <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
        <Phone className="w-3 h-3" /> {pedido.cliente?.telefone}
      </div>

      {pedido.tipo === "entrega" && endereco && (
        <div className="text-xs text-[var(--text-secondary)] flex items-start gap-1 mt-1">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="truncate">
            {[endereco.logradouro, endereco.numero, endereco.bairro]
              .filter(Boolean)
              .join(", ") || "—"}
          </span>
        </div>
      )}

      <ul className="mt-2 space-y-1 text-xs">
        {pedido.itens?.slice(0, 3).map((it, i) => (
          <li key={i} data-testid={`pedido-item-${pedido.id}-${i}`}>
            <div>
              <span className="font-semibold">{it.quantidade}×</span> {it.nome}
              {it.unidade_medida && it.unidade_medida !== "unidade" && (
                <span className="text-[var(--text-secondary)]"> ({it.unidade_medida})</span>
              )}
              {it.tipo === "encomenda" && it.data_entrega_agendada && (
                <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                  📅 {it.data_entrega_agendada}
                </span>
              )}
            </div>
            {(it.complementos_escolhidos || []).length > 0 && (
              <div className="pl-3 border-l-2 border-orange-200 text-[11px] text-[var(--text-secondary)] mt-0.5">
                {it.complementos_escolhidos.map((c, k) => (
                  <div key={k}>
                    <span className="font-medium">{c.grupo_nome}:</span> {c.opcao_nome}
                    {c.preco_adicional > 0 && (
                      <span className="text-[10px]"> (+{formatBRL(c.preco_adicional)})</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {it.observacao_item && (
              <p className="pl-3 text-[11px] italic text-amber-700 mt-0.5">
                Obs: {it.observacao_item}
              </p>
            )}
          </li>
        ))}
        {pedido.itens?.length > 3 && (
          <li className="text-[var(--text-secondary)]">
            +{pedido.itens.length - 3} item(ns)…
          </li>
        )}
      </ul>

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--border)]">
        <span className="text-xs flex items-center gap-1 text-[var(--text-secondary)]">
          <CreditCard className="w-3 h-3" /> {pedido.forma_pagamento}
        </span>
        <span className="font-bold text-sm">{formatBRL(pedido.total)}</span>
      </div>

      {pedido.observacoes && (
        <p className="mt-2 text-xs italic text-amber-700 bg-amber-50 rounded px-2 py-1">
          “{pedido.observacoes}”
        </p>
      )}

      {!readOnly && (
        <div className="flex gap-2 mt-2">
          {proxStatus && (
            <button
              onClick={() => onAdvance(pedido)}
              className="flex-1 px-2 py-1.5 text-xs bg-[var(--primary)] text-white rounded hover:opacity-90"
              data-testid={`delivery-advance-${pedido.id}`}
            >
              {proxStatus === "aceito" && "Aceitar"}
              {proxStatus === "em_preparo" && "Iniciar preparo"}
              {proxStatus === "pronto" && "Marcar pronto"}
              {proxStatus === "entregue" && "Entregue ✓"}
            </button>
          )}
          {pedido.status !== "entregue" && pedido.status !== "cancelado" && (
            <button
              onClick={() => onCancel(pedido)}
              className="px-2 py-1.5 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50"
              data-testid={`delivery-cancel-${pedido.id}`}
            >
              <XCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LoyaltyModal({ loyalty, loading, onClose }) {
  const { pedido, result } = loyalty || {};
  const cliente = pedido?.cliente || {};

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="delivery-loyalty-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Cupom de fidelidade</p>
              <h2 className="text-xl font-bold flex items-center gap-2 mt-1">
                <Gift className="w-5 h-5" /> Cliente recorrente
              </h2>
              <p className="text-xs opacity-90 mt-1">
                {cliente.nome} • {pedido?.cliente_pedidos_count}º pedido
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-8 text-[var(--text-secondary)]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Gerando cupom personalizado...
            </div>
          )}

          {!loading && result && (
            <>
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                Cupom criado com sucesso! Use os botões abaixo para enviar ao cliente.
              </p>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-orange-300 rounded-xl p-4 text-center mb-4">
                <div className="text-[10px] uppercase text-orange-700 font-bold tracking-wider">
                  Código exclusivo
                </div>
                <div className="text-2xl font-mono font-bold text-orange-700 my-1 tracking-wider">
                  {result.cupom.codigo}
                </div>
                <div className="text-sm font-medium text-orange-700">
                  {Math.round(result.cupom.valor)}% de desconto · 1 uso
                </div>
                <div className="text-[10px] text-[var(--text-secondary)] mt-1">
                  Válido até {result.cupom.valido_ate?.slice(0, 10)}
                </div>
                <button
                  onClick={() => copiar(result.cupom.codigo)}
                  className="mt-3 px-3 py-1 text-xs bg-white border border-orange-300 text-orange-700 rounded inline-flex items-center gap-1 hover:bg-orange-50"
                  data-testid="delivery-loyalty-copy-codigo"
                >
                  <Copy className="w-3 h-3" /> Copiar código
                </button>
              </div>

              {result.email_sent && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded p-2 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Email enviado automaticamente para {cliente.email}
                </div>
              )}

              <div className="space-y-2">
                <a
                  href={result.wa_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-3 bg-emerald-500 text-white rounded-lg font-medium text-center hover:bg-emerald-600 inline-flex items-center justify-center gap-2"
                  data-testid="delivery-loyalty-whatsapp"
                >
                  <Send className="w-4 h-4" /> Enviar pelo WhatsApp
                </a>
                <button
                  onClick={() => copiar(result.mensagem_sugerida)}
                  className="block w-full px-4 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
                  data-testid="delivery-loyalty-copy-msg"
                >
                  Copiar mensagem sugerida
                </button>
              </div>

              <p className="text-[10px] text-[var(--text-secondary)] text-center mt-4">
                O cupom já está ativo no checkout do seu cardápio. Limite: 1 uso por
                este cliente.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

